import * as React from "react";
import UIIcon, { UIIconProps } from "common/blocks/UIIcon";
import DefaultErrorView, { DefaultErrorViewProps } from "../TextInput/DefaultErrorView";

import classNames from "classnames/bind";
import styles from "./Dropdown.module.scss";

const cx = classNames.bind(styles);

export interface Option {
    value: string | number | undefined;
    label: string;
    data?: any;
}

export interface GroupedOption {
    label: string;
    options: Option[];
}

export type DropdownIconProps = {
    uiIcon: UIIconProps;
    onClick?: () => void;
};

export interface DropdownProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
    className?: string;
    ariaLabel?: string;
    errorText?: string;
    hasError?: boolean;
    helperText?: string;
    id?: string;
    isDisabled?: boolean;
    isRequired?: boolean;
    label?: string;
    name?: string;
    options: Array<Option> | Array<GroupedOption>;
    value?: Option["value"];
    outerLabelClassName?: string;
    borderOnError?: boolean;
    errorProps?: DefaultErrorViewProps;
    helperTextPlacement?: "before" | "after";
    dropdownIcon?: DropdownIconProps;
    loading?: boolean;
}

const Dropdown = React.forwardRef<HTMLSelectElement, DropdownProps>((props, ref) => {
    const {
        label,
        options,
        isDisabled = false,
        isRequired = false,
        hasError = false,
        id,
        defaultValue,
        onChange,
        errorText,
        helperText,
        className,
        value,
        ariaLabel,
        outerLabelClassName,
        borderOnError,
        errorProps,
        helperTextPlacement = "after",
        dropdownIcon,
        loading,
        ...rest
    } = props;

    const Label = !label ? "div" : "label";
    const showHelperText = helperText && !hasError;

    return (
        <Label className={cx("container", outerLabelClassName)}>
            {label && <span className={cx("label", { isRequired })}>{label}</span>}

            <div
                className={(cx("dropdown"), cx(!!dropdownIcon ? "dropdown--informationIcon" : ""))}
            >
                {showHelperText && helperTextPlacement === "before" && (
                    <span className={cx("helperTextBefore")}>{helperText}</span>
                )}
                <div className={cx("dropdown--informationIcon__selector")}>
                    {!loading && (
                        <UIIcon
                            color="black"
                            className={cx("nativeDropdownIndicator")}
                            icon="ChevronDown"
                        />
                    )}
                    {loading && (
                        <UIIcon icon="CircleAnimation" className={cx("loadingAnimation")} />
                    )}
                    <select
                        ref={ref}
                        id={id}
                        disabled={isDisabled}
                        className={cx(
                            "nativeSelect",
                            className,
                            !!borderOnError && !!hasError ? "redBorder" : ""
                        )}
                        onChange={onChange}
                        aria-label={ariaLabel}
                        defaultValue={defaultValue}
                        value={value}
                        data-loading={loading}
                        {...rest}
                    >
                        {loading && (
                            <option selected value="loading">
                                Loading...
                            </option>
                        )}
                        {!loading && defaultValue === undefined && (
                            <option disabled value={defaultValue} />
                        )}
                        {!loading &&
                            options.map((option) => {
                                if ("options" in option) {
                                    return (
                                        <optgroup key={option.label} label={option.label}>
                                            {option.options.map((opt) => (
                                                <option
                                                    disabled={opt.value === ""}
                                                    key={opt.value}
                                                    value={opt.value}
                                                >
                                                    {opt.label}
                                                </option>
                                            ))}
                                        </optgroup>
                                    );
                                }
                                return (
                                    <option
                                        disabled={option.value === ""}
                                        key={option.label}
                                        value={option.value ?? ""}
                                    >
                                        {option.label}
                                    </option>
                                );
                            })}
                    </select>
                </div>
                {!!dropdownIcon && (
                    <div
                        className={cx("dropdown--informationIcon__icon")}
                        onClick={dropdownIcon.onClick}
                    >
                        <UIIcon {...dropdownIcon.uiIcon}></UIIcon>
                    </div>
                )}
            </div>

            {showHelperText && helperTextPlacement === "after" && (
                <span className={cx("helperTextAfter")}>{helperText}</span>
            )}
            {hasError && <DefaultErrorView errorText={errorText} {...errorProps} />}
        </Label>
    );
});

Dropdown.displayName = "Dropdown";

export default Dropdown;
